import { useMemo, useEffect } from 'react'
import { IntlProvider } from 'react-intl'
import { useRouter } from 'next/router'
import Script from 'next/script'
import type { AppProps } from 'next/app'
import { ThemeProvider } from 'theme-ui'
import { NextUIProvider } from '@nextui-org/react'
import {
  ModalContextProvider,
  MyReportsContextProvider,
  GetReportsModal,
} from '@propertylens/ui-kit'

import { useStytchUser } from '@stytch/nextjs'
import '../global.css'
import '../carousel.css'
import '../faq-accordion.css'

import Layout from '~/components/Layout'
import PageTracker from '~/components/PageTracker'

import English from '../locales/en.json'
import orgSchema from '../data/orgSchema.json'
import theme from '../src/theme'

import '@builder.io/widgets/dist/lib/builder-widgets-async'
import StytchProvider from '~/context/stytch'
import { segmentAnalytics } from '~/lib/segmentAnalytics'
import ErrorBoundary from './ErrorBoundary'
import { GoogleTagManager } from '@next/third-parties/google'
const App = ({ Component, pageProps }: AppProps) => {
  const { locale, defaultLocale, push } = useRouter()
  const [shortLocale] = locale ? locale.split('-') : ['en']

  const messages = useMemo(() => {
    switch (shortLocale) {
      case 'en':
        return English
      default:
        return English
    }
  }, [shortLocale])

  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://cdn.jsdelivr.net/npm/remixicon@4.5.0/fonts/remixicon.css';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }, []);

  return (
    <ErrorBoundary>
      <StytchProvider pageProps={pageProps}>
            <Script
              id="orgSchema"
              type="application/ld+json"
              strategy="afterInteractive"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(orgSchema),
              }}
            />
            <IntlProvider
              locale={locale}
              defaultLocale={defaultLocale}
              messages={messages}
            >
              <NextUIProvider navigate={push}>
                <ThemeProvider theme={theme}>
                  <ModalContextProvider>
                    <MyReportsContextProvider>
                      <PageTracker>
                        <Layout {...pageProps}>
                          {/* // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            @ts-ignore */}
                          <Component {...pageProps} />
                          <GoogleTagManager gtmId={process.env.NEXT_PUBLIC_GOOGLE_TAGS_ID} />
                          {process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY && (
                            <GetReportsModal
                              navigate={push}
                              googleAPIKey={
                                process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY
                              }
                              authProvider={useStytchUser}
                              track={segmentAnalytics.track}
                            />
                          )}
                        </Layout>
                      </PageTracker>
                    </MyReportsContextProvider>
                  </ModalContextProvider>
                </ThemeProvider>
              </NextUIProvider>
            </IntlProvider>
          </StytchProvider>
    </ErrorBoundary>
    
  )
}

export default App
